import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail } from "lucide-react";
import { WHATSAPP_URL, WHATSAPP_DISPLAY, CONTACT_EMAIL } from "../content";
import { Reveal } from "../lib/motion";
import { WhatsAppIcon } from "../lib/icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-tinta border border-inkline text-creme placeholder:text-muteddark/60 font-inter text-sm px-4 h-12 focus:border-lima focus:outline-none transition-colors duration-150";

const INITIAL = {
  nome: "",
  empresa: "",
  whatsapp: "",
  email: "",
  ticket_medio: "",
  mensagem: "",
};

export const Contato = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contato`, form);
      toast.success("Diagnóstico solicitado. Abrindo o WhatsApp para concluir.");
      window.open(data.whatsapp_url || WHATSAPP_URL, "_blank", "noopener");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Não foi possível enviar. Chame a gente no WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contato"
      data-testid="contato-section"
      className="relative bg-tinta py-20 md:py-32 overflow-hidden"
    >
      <div
        className="absolute -right-20 bottom-16 w-72 h-14 bg-lima hidden lg:block"
        style={{ clipPath: "polygon(14% 0, 100% 0, 86% 100%, 0 100%)", transform: "rotate(-8deg)" }}
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="label-caps text-lima mb-5">Contato</p>
            <h2
              data-testid="contato-title"
              className="headline text-creme text-3xl sm:text-4xl lg:text-5xl"
            >
              Agende seu diagnóstico
            </h2>
            <p className="mt-6 text-muteddark text-base leading-relaxed max-w-md">
              Preencha o formulário ou chame direto no WhatsApp. Resposta da
              liderança, sem intermediário.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 space-y-4">
              <a
                data-testid="contato-whatsapp-link"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 border border-inkline p-5 hover:border-lima transition-colors duration-150 group"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-lima text-tinta">
                  <WhatsAppIcon className="w-5 h-5" />
                </span>
                <span>
                  <span className="block label-caps text-muteddark !text-[10px]">WhatsApp comercial</span>
                  <span className="block font-sora font-bold text-creme mt-1 group-hover:text-lima transition-colors duration-150">
                    {WHATSAPP_DISPLAY}
                  </span>
                </span>
              </a>
              <a
                data-testid="contato-email-link"
                href={`mailto:${CONTACT_EMAIL}`}
                className="flex items-center gap-4 border border-inkline p-5 hover:border-lima transition-colors duration-150 group"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-lima text-tinta">
                  <Mail className="w-5 h-5" />
                </span>
                <span>
                  <span className="block label-caps text-muteddark !text-[10px]">E-mail comercial</span>
                  <span className="block font-sora font-bold text-creme mt-1 group-hover:text-lima transition-colors duration-150">
                    {CONTACT_EMAIL}
                  </span>
                </span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            data-testid="contato-form"
            onSubmit={submit}
            className="border border-inkline p-6 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            <div>
              <label htmlFor="campo-nome" className="label-caps text-muteddark !text-[10px] block mb-2">Nome</label>
              <input id="campo-nome" data-testid="form-nome" required value={form.nome} onChange={set("nome")} placeholder="Seu nome" className={inputCls} />
            </div>
            <div>
              <label htmlFor="campo-empresa" className="label-caps text-muteddark !text-[10px] block mb-2">Empresa</label>
              <input id="campo-empresa" data-testid="form-empresa" required value={form.empresa} onChange={set("empresa")} placeholder="Nome da empresa" className={inputCls} />
            </div>
            <div>
              <label htmlFor="campo-whatsapp" className="label-caps text-muteddark !text-[10px] block mb-2">WhatsApp</label>
              <input id="campo-whatsapp" data-testid="form-whatsapp" required value={form.whatsapp} onChange={set("whatsapp")} placeholder="(00) 00000-0000" className={inputCls} />
            </div>
            <div>
              <label htmlFor="campo-email" className="label-caps text-muteddark !text-[10px] block mb-2">E-mail</label>
              <input id="campo-email" data-testid="form-email" type="email" required value={form.email} onChange={set("email")} placeholder="voce@empresa.com.br" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="campo-ticket" className="label-caps text-muteddark !text-[10px] block mb-2">Ticket médio do seu produto</label>
              <input id="campo-ticket" data-testid="form-ticket" value={form.ticket_medio} onChange={set("ticket_medio")} placeholder="Ex.: R$ 5.000" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="campo-mensagem" className="label-caps text-muteddark !text-[10px] block mb-2">Mensagem</label>
              <textarea id="campo-mensagem" data-testid="form-mensagem" rows={4} value={form.mensagem} onChange={set("mensagem")} placeholder="Conte em duas linhas o seu desafio de crescimento." className={`${inputCls} h-auto py-3 resize-none`} />
            </div>
            <div className="sm:col-span-2">
              <button
                data-testid="form-submit-button"
                type="submit"
                disabled={sending}
                className="w-full bg-lima text-tinta font-sora font-bold text-sm tracking-[-0.02em] py-4 inline-flex items-center justify-center gap-2 hover:bg-[#d4ff66] disabled:opacity-60 transition-colors duration-150"
              >
                {sending ? "Enviando..." : "Quero meu diagnóstico"} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-4 text-[11px] text-muteddark/70 leading-relaxed">
                Ao enviar, você concorda com a nossa{" "}
                <a href="/privacidade" className="underline hover:text-lima">Política de Privacidade</a> (LGPD).
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
